import React, { useCallback, useMemo, useState } from 'react';
import {
  AppCheckbox,
  AppDatePicker,
  AppInputNumber,
  AppModal,
  AppRemixIcon,
  AppSingleSelect,
} from '@/layout/AppComponents';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import { axiosPath } from '../../../../../../routing/helpers/router';
import errorHandler from '@/layout/components/error/ErrorHandler';
import Translator from '../../../../../components/helpers/Translator';
import * as currentPageAction from '../../../../../../store/actions/currentPageAction';

export default function MassiveObligationNextDateSettings({
  loadObligationTab,
  elementID,
}) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const checkedResources = useSelector(
    (state) => state.currentPage?.checkedResources?.[elementID]
  );
  const [showModal, setShowModal] = useState(false);
  const [periodOptions] = useState([
    { label: 'días', value: 'days' },
    { label: 'semanas', value: 'weeks' },
    { label: 'meses', value: 'months' },
    { label: 'años', value: 'years' },
  ]);
  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      periodType: Yup.string().when('noNextDate', {
        is: (v) => v === false || v === 'false' || v === 0 || v === '0',
        then: (s) =>
          s.required(
            ReactDOMServer.renderToString(
              <Translator
                file="module-resourceManager"
                name="resource.required"
              />,
            ),
          ),
        otherwise: (s) => s.notRequired(),
      }),
      period: Yup.string().when('noNextDate', {
        is: (v) => v === false || v === 'false' || v === 0 || v === '0',
        then: (s) =>
          s.required(
            ReactDOMServer.renderToString(
              <Translator
                file="module-resourceManager"
                name="resource.required"
              />,
            ),
          ),
        otherwise: (s) => s.notRequired(),
      }),
      firstPeriodStartDate: Yup.string().when('noNextDate', {
        is: (v) => v === false || v === 'false' || v === 0 || v === '0',
        then: (s) =>
          s.required(
            ReactDOMServer.renderToString(
              <Translator
                file="module-resourceManager"
                name="resource.required"
              />,
            ),
          ),
        otherwise: (s) => s.notRequired(),
      }),
    });
  }, []);


  // > UPDATE NEXT DATE SETTINGS
  const updateNextDateSettings = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath(
            "api_r_user_module_resourceManager_resourceObligation_massive_nextDate_set"
          ),
          {
            moduleBridgeHex,
            resourceObligationIDs: checkedResources,
            payload,
          }
        )
        .then(() => {
          dispatch(currentPageAction.change("updateCard", elementID, true));
          setShowModal(false);
          loadObligationTab();
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, checkedResources, loadObligationTab]
  );
  // < UPDATE NEXT DATE SETTINGS

  return (
    <>
      <button
        type="button"
        className="btn btn-xs btn-warning mr-2"
        onClick={() => setShowModal(true)}
        disabled={!checkedResources || checkedResources?.length === 0}
      >
        <AppRemixIcon icon="settings-5" className="mr-1" />
        <Translator
          file="module-resourceManager"
          name="resource.bulk.configuration"
        />
      </button>
      <AppModal
        title={
          <Translator
            file="module-resourceManager"
            name="resource.set.frequency"
          />
        }
        show={showModal}
        onClose={() => setShowModal(false)}
        size="lg"
      >
        <Formik
          initialValues={{
            periodType: 'months',
            period: 1,
            firstPeriodStartDate: '',
            noNextDate: false,
          }}
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={validationSchema}
          onSubmit={updateNextDateSettings}
        >
          {({ values, setFieldValue, errors, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-6">
                  <AppSingleSelect
                    title={
                      <Translator
                        file="module-resourceManager"
                        name="resource.frequency"
                      />
                    }
                    icon={<AppRemixIcon icon="calendar-event" />}
                    options={periodOptions}
                    value={values.periodType}
                    onChange={(v) => setFieldValue('periodType', v)}
                    error={errors.periodType}
                    isDisabled={values.noNextDate}
                  />
                  <AppInputNumber
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-resourceManager"
                        name="resource.number.of.options.type"
                        variables={{
                          periodType: periodOptions.find(
                            (p) => p.value === values.periodType
                          )?.label,
                        }}
                      />
                    )}
                    value={values.period}
                    icon={<AppRemixIcon icon="calendar-2" />}
                    onChange={(v) => setFieldValue('period', v)}
                    error={errors.period}
                    disabled={values.noNextDate}
                  />
                  <AppDatePicker
                    title={
                      <Translator
                        file="module-resourceManager"
                        name="resource.start.date"
                      />
                    }
                    value={values.firstPeriodStartDate}
                    onChange={(v) => setFieldValue('firstPeriodStartDate', v)}
                    error={errors.firstPeriodStartDate}
                    popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                    showYearDropdown
                    showMonthDropdown
                    hasToggleType
                    isDisabled={values.noNextDate}
                  />
                </div>
                <div className="col-6">
                  <AppCheckbox
                    value={values.noNextDate}
                    onChange={(v) => setFieldValue('noNextDate', v)}
                    hasBorder
                  >
                    <Translator
                      file="module-resourceManager"
                      name="resource.no.regularity"
                    />
                  </AppCheckbox>
                  {values.noNextDate && (
                    <div className="alert alert-info">
                      <Translator
                        file="module-resourceManager"
                        name="resource.setting.no.periodicity.established.worldlex"
                      />
                    </div>
                  )}
                  {values.firstPeriodStartDate && values.period && (
                    <ul>
                      <li>
                        <Translator
                          file="module-resourceManager"
                          name="resource.first.period"
                        />
                        : <br />
                        <span>
                          {`De ${values.firstPeriodStartDate} a ${moment(
                            values.firstPeriodStartDate,
                            'DD/MM/YYYY'
                          )
                            .add(parseInt(values.period, 10), values.periodType)
                            .subtract(1, 'days')
                            .format('DD/MM/YYYY')} inclusive.`}
                        </span>
                      </li>
                      <li>
                        Siguiente periodo: <br />
                        <span>
                          {`De ${moment(
                            values.firstPeriodStartDate,
                            'DD/MM/YYYY'
                          )
                            .add(parseInt(values.period, 10), values.periodType)
                            .format('DD/MM/YYYY')} a ${moment(
                            values.firstPeriodStartDate,
                            'DD/MM/YYYY'
                          )
                            .add(
                              parseInt(values.period, 10) * 2,
                              values.periodType
                            )
                            .subtract(1, 'days')
                            .format('DD/MM/YYYY')} inclusive.`}
                        </span>
                      </li>
                    </ul>
                  )}
                </div>
              </div>
              <button
                type="submit"
                className="btn btn-sm btn-primary float-right"
              >
                <Translator
                  file="module-resourceManager"
                  name="resource.configure"
                />
              </button>
            </form>
          )}
        </Formik>
      </AppModal>
    </>
  );
}
